// Item rendering functions - アイテム描画処理

import { getP5 } from '../../core/p5-instance';
import { getItems } from '../../store';
import type { Item } from '../../types/game-types';

/**
 * drawItems() - 全アイテムの描画
 * パワー、スコア、シールド、ボム、ホーミングアイテムを描画
 */
export function drawItems() {
    const p = getP5();
    for (const item of getItems()) {
        p.push();
        p.translate(item.x, item.y);
        p.rotate(item.rotation);

        // グロー効果
        p.fill(p.red(item.color), p.green(item.color), p.blue(item.color), 50);
        p.circle(0, 0, item.size * 1.5);

        // アイテム本体
        p.fill(item.color);
        p.stroke(255);
        p.strokeWeight(2);

        // アイテムタイプ別の描画
        if (item.type === 'power') {
            drawPowerItem(item);
        } else if (item.type === 'score') {
            drawScoreItem(item);
        } else if (item.type === 'bomb' || item.type === 'homing' || item.type === 'cannon') {
            drawSpecialItem(item);
        } else {
            // シールド（バリアドーム）
            drawShieldItem(item);
        }

        p.pop();
    }
}

/**
 * drawPowerItem() - パワーアップアイテム（二重シェブロン上向き矢印）
 */
function drawPowerItem(item: Item) {
    const p = getP5();
    const s = item.size / 2;
    const chevW = s * 0.7;   // シェブロン横幅の半分
    const chevH = s * 0.4;   // シェブロン高さ
    const thick = s * 0.2;   // シェブロンの線の太さ
    const gap = s * 0.5;     // 上下シェブロン間隔
    const topY = -(chevH + gap) / 2;  // 中央寄せ基準

    // 上のシェブロン
    p.beginShape();
    p.vertex(0, topY);                        // 頂点
    p.vertex(chevW, topY + chevH);            // 右端
    p.vertex(chevW - thick, topY + chevH);    // 右内側
    p.vertex(0, topY + thick);                // 頂点内側
    p.vertex(-chevW + thick, topY + chevH);   // 左内側
    p.vertex(-chevW, topY + chevH);           // 左端
    p.endShape(p.CLOSE);

    // 下のシェブロン
    const botY = topY + gap;
    p.beginShape();
    p.vertex(0, botY);                        // 頂点
    p.vertex(chevW, botY + chevH);            // 右端
    p.vertex(chevW - thick, botY + chevH);    // 右内側
    p.vertex(0, botY + thick);                // 頂点内側
    p.vertex(-chevW + thick, botY + chevH);   // 左内側
    p.vertex(-chevW, botY + chevH);           // 左端
    p.endShape(p.CLOSE);
}

/**
 * drawScoreItem() - スコアアイテム（ダイヤモンド型カット宝石）
 */
function drawScoreItem(item: Item) {
    const p = getP5();
    const s = item.size / 2;
    const crownH = s * 0.35;   // クラウン（上部）の高さ
    const topW = s * 0.5;      // 上辺の幅の半分
    const midW = s * 0.85;     // 中間（最大幅）の半分

    // 上部クラウン（台形）
    p.beginShape();
    p.vertex(-topW, -s);       // 左上
    p.vertex(topW, -s);        // 右上
    p.vertex(midW, -s + crownH);  // 右中間
    p.vertex(-midW, -s + crownH); // 左中間
    p.endShape(p.CLOSE);

    // 下部パビリオン（逆三角形）
    p.beginShape();
    p.vertex(-midW, -s + crownH); // 左中間
    p.vertex(midW, -s + crownH);  // 右中間
    p.vertex(0, s);                // 底頂点
    p.endShape(p.CLOSE);

    // ファセットライン（カット面の分割線）
    p.stroke(255, 255, 255, 150);
    p.strokeWeight(1);
    p.line(0, -s, 0, -s + crownH);              // 中央縦線（クラウン）
    p.line(-midW, -s + crownH, 0, s);            // 左ファセット
    p.line(midW, -s + crownH, 0, s);             // 右ファセット
}

/**
 * drawShieldItem() - シールドアイテム（バリアドーム型）
 */
function drawShieldItem(item: Item) {
    const p = getP5();
    const s = item.size / 2;
    const coreR = s * 0.25;  // コアの半径
    const arcR = s * 0.85;   // バリア弧線の半径

    // 中央コア（小さな円）
    p.fill(item.color);
    p.stroke(255);
    p.strokeWeight(2);
    p.circle(0, 0, coreR * 2);

    // バリアフィールド（上部弧線）
    p.noFill();
    p.stroke(p.red(item.color), p.green(item.color), p.blue(item.color), 200);
    p.strokeWeight(2.5);
    p.arc(0, 0, arcR * 2, arcR * 2, -p.PI * 0.85, -p.PI * 0.15);

    // バリアフィールド（下部弧線）
    p.arc(0, 0, arcR * 2, arcR * 2, p.PI * 0.15, p.PI * 0.85);

    // 外側のバリアリング（薄い）
    p.stroke(255, 255, 255, 100);
    p.strokeWeight(1.5);
    p.arc(0, 0, s * 2, s * 2, -p.PI * 0.75, -p.PI * 0.25);
    p.arc(0, 0, s * 2, s * 2, p.PI * 0.25, p.PI * 0.75);
}

/**
 * drawSpecialItem() - 特殊兵装アイテム共通（六角カプセル形状）
 * bomb/homing/cannon を統一アイコンで描画。色のみで機体を区別。
 */
function drawSpecialItem(item: Item) {
    const p = getP5();
    const s = item.size / 2;

    // 六角カプセル
    p.fill(item.color);
    p.stroke(255);
    p.strokeWeight(2);
    p.beginShape();
    for (let i = 0; i < 6; i++) {
        const angle = p.TWO_PI / 6 * i - p.HALF_PI;
        p.vertex(p.cos(angle) * s * 0.8, p.sin(angle) * s * 0.8);
    }
    p.endShape(p.CLOSE);

    // 中央の菱形マーク
    p.fill(255, 255, 255, 200);
    p.noStroke();
    const d = s * 0.35;
    p.beginShape();
    p.vertex(0, -d);
    p.vertex(d * 0.7, 0);
    p.vertex(0, d);
    p.vertex(-d * 0.7, 0);
    p.endShape(p.CLOSE);
}
